/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useRef } from "react";
import { ArrowsUpDownIcon, PlusIcon } from "@heroicons/react/24/outline";
import CourieButton from "@src/common/components/Button/Button";
import React from "react";
import SortField, { SortFieldOption } from "./SortField";
import { useTableControl } from "../context/TableControlContext";

import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverHeading,
  PopoverDescription,
} from "@src/common/components/Popover";
import {
  ShipmentsSortField,
  ShipmentsSorting,
  SortingDirection,
} from "@api/graphql/generated/generated-types";

export type PartialShipmentsSorting = {
  direction: SortingDirection;
  field?: ShipmentsSortField;
};

type Props = {
  sortFieldOptions: SortFieldOption[];
  // Icon-only trigger for tight headers (e.g. the Routes Map list).
  iconOnly?: boolean;
};

function SortingOptionsDropdown({
  sortFieldOptions = [],
  iconOnly = false,
}: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const { sortFields, setSortFields } = useTableControl();
  const [selectedSorts, setSelectedSorts] = useState<PartialShipmentsSorting[]>(
    [{ direction: SortingDirection.Asc, field: undefined }]
  );
  
  // Ref to prevent feedback loop between effects
  const isSyncingFromSortFields = useRef(false);

  // Initialize selectedSorts from sortFields on mount
  useEffect(() => {
    if (sortFields.length > 0) {
      setSelectedSorts(sortFields as PartialShipmentsSorting[]);
    }
  }, []);

  // Sync sortFields → selectedSorts (external changes like saved views)
  useEffect(() => {
    const filteredSorts: ShipmentsSorting[] = selectedSorts.filter(
      (sort) => sort.field
    ) as ShipmentsSorting[];

    if (JSON.stringify(filteredSorts) !== JSON.stringify(sortFields)) {
      // Mark that we're syncing from sortFields to prevent Effect below from firing back
      isSyncingFromSortFields.current = true;
      setSelectedSorts(sortFields.length > 0 ? sortFields as PartialShipmentsSorting[] : [{ direction: SortingDirection.Asc, field: undefined }]);
    }
  }, [sortFields]);

  // Sync selectedSorts → sortFields (user interactions in dropdown)
  useEffect(() => {
    // Skip if we're syncing from sortFields (prevents feedback loop)
    if (isSyncingFromSortFields.current) {
      isSyncingFromSortFields.current = false;
      return;
    }

    const filteredSorts: ShipmentsSorting[] = selectedSorts.filter(
      (sort) => sort.field
    ) as ShipmentsSorting[];

    if (
      JSON.stringify(filteredSorts) !== JSON.stringify(sortFields) &&
      filteredSorts.length > 0
    ) {
      setSortFields(filteredSorts);
    }
  }, [selectedSorts]);

  const addSelectedSort = () => {
    setSelectedSorts([
      ...selectedSorts,
      { field: undefined, direction: SortingDirection.Asc },
    ]);
  };

  const updateSelectedSort = (index: number, sort: ShipmentsSorting) => {
    const updatedSorts = [...selectedSorts];
    updatedSorts[index] = sort;
    setSelectedSorts(updatedSorts);
  };

  const removeSelectedSort = (index: number) => {
    const removedSort = selectedSorts[index];
    if (removedSort.field) {
      setSortFields(
        selectedSorts.filter((_, i) => i !== index) as ShipmentsSorting[]
      );
    }
    setSelectedSorts(selectedSorts.filter((_, i) => i !== index));
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger>
        <CourieButton
          outline={sortFields.length > 0 ? false : true}
          size="xs"
          color={sortFields.length > 0 ? "yellow" : "secondary"}
          onClick={() => setIsOpen((v) => !v)}
          aria-label="Sort"
          title={
            sortFields.length > 0
              ? `Sorted by ${sortFields.length} ${sortFields.length > 1 ? "fields" : "field"}`
              : "Sort"
          }
        >
          <ArrowsUpDownIcon className={iconOnly ? "h-4 w-4" : "h-4 w-4 mr-1"} />
          {iconOnly ? null : sortFields.length > 0 ? (
            <span className="text-primary truncate dark:text-primary-400">
              Sorted by {sortFields.length}{" "}
              {sortFields.length > 1 ? "fields" : "field"}
            </span>
          ) : (
            <span className="dark:text-gray-200">Sort</span>
          )}
        </CourieButton>
      </PopoverTrigger>

      <PopoverContent
        style={{ left: 100 }}
        className="absolute z-30 bg-white dark:bg-gray-800 border dark:border-gray-700 min-w-max p-3 shadow-2xl rounded-md"
      >
        <PopoverHeading className="mb-3 dark:text-gray-100">
          Sort By
        </PopoverHeading>
        <PopoverDescription>
          <div className="flex flex-col gap-3">
            {selectedSorts.map((sort, index) => (
              <SortField
                sortFieldOptions={sortFieldOptions}
                key={index}
                sort={sort}
                index={index}
                onRemove={removeSelectedSort}
                onUpdate={updateSelectedSort}
              />
            ))}
            <div>
              <CourieButton
                size={"xs"}
                onClick={() => addSelectedSort()}
                color={"secondary"}
              >
                <PlusIcon className="h-4 w-4 mr-1" />
                {selectedSorts.length > 0 ? "Add another sort" : "Add sort"}
              </CourieButton>
            </div>
          </div>
        </PopoverDescription>
      </PopoverContent>
    </Popover>
  );
}

export default SortingOptionsDropdown;
